import React, { lazy, ReactNode, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';

import Loading from '../components/Loading';
import ProtectedRoute from '../components/ProtectedRoute';
import RootRoute from '../components/RootRoute';
import Layout from '../layout/Layout';

// Lazy load all pages
const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/Register'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const SetPassword = lazy(() => import('../pages/SetPassword'));
const VerifyEmail = lazy(() => import('../pages/VerifyEmail'));
const RegistrationSuccess = lazy(() => import('../pages/RegistrationSuccess'));
const SubscriptionOffers = lazy(() => import('../pages/SubscriptionOffers'));
const MyPlans = lazy(() => import('../pages/MyPlans'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Payment Pages
const PaymentSuccess = lazy(() => import('../pages/Payment/Success'));
const PaymentCancelled = lazy(() => import('../pages/Payment/Cancelled'));
const PaymentExpired = lazy(() => import('../pages/Payment/Expired'));

// Extra Income Pages
const ExtraIncomeIntroduction = lazy(
  () => import('../pages/ExtraIncome/Introduction')
);
const ExtraIncomeQuestionnaire = lazy(
  () => import('../pages/ExtraIncome/Questionnaire')
);
const ExtraIncomeSuggestions = lazy(
  () => import('../pages/ExtraIncome/Suggestions')
);
const ExtraIncomeStrategies = lazy(
  () => import('../pages/ExtraIncome/Strategies')
);
// Lazy loading com retry para ActionPlan
const BusinessPlanActionPlan = lazy(() => {
  return import('../pages/BusinessPlan/ActionPlan').catch((error) => {
    console.error('Error loading ActionPlan:', error);

    // Retry uma vez após 500ms
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        import('../pages/BusinessPlan/ActionPlan')
          .then(resolve)
          .catch((retryError) => {
            console.error('ActionPlan retry failed:', retryError);

            // Se falhar novamente, retorna um componente de fallback
            resolve({
              default: () => (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-yellow-600 text-2xl">🔧</span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                      Plano de Ação Temporariamente Indisponível
                    </h2>
                    <p className="text-gray-600 mb-4">
                      Estamos atualizando esta funcionalidade. Tente novamente em alguns minutos.
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Recarregar Página
                    </button>
                  </div>
                </div>
              )
            });
          });
      }, 500);
    });
  });
});

// Business Plan Pages
const BusinessPlanNew = lazy(() => import('../pages/BusinessPlan/New'));
const BusinessPlanExecutiveSummary = lazy(
  () => import('../pages/BusinessPlan/ExecutiveSummary')
);
const BusinessPlanMissionVisionValues = lazy(
  () => import('../pages/BusinessPlan/MissionVisionValues')
);
const BusinessPlanProductsServices = lazy(
  () => import('../pages/BusinessPlan/ProductsServices')
);
const BusinessPlanMarketAnalysis = lazy(
  () => import('../pages/BusinessPlan/MarketAnalysis')
);
const BusinessPlanMarketingSales = lazy(
  () => import('../pages/BusinessPlan/MarketingSales')
);
const BusinessPlanOperationalStructure = lazy(
  () => import('../pages/BusinessPlan/OperationalStructure')
);
const BusinessPlanRisksExpansion = lazy(
  () => import('../pages/BusinessPlan/RisksExpansion')
);
const BusinessPlanChallengesObjectives = lazy(
  () => import('../pages/BusinessPlan/ChallengesObjectives')
);
const BusinessPlanFinances = lazy(
  () => import('../pages/BusinessPlan/Finances')
);

// Finance Management Pages
const FinanceManagementDashboard = lazy(
  () => import('../pages/FinanceManagement/Dashboard')
);
const BanksGoals = lazy(
  () => import('../pages/FinanceManagement/BanksGoals')
);
const TransactionHistory = lazy(
  () => import('../pages/FinanceManagement/TransactionHistory')
);
const FinanceManagementLayout = lazy(
  () => import('../components/FinanceManagement/FinanceManagementLayout')
);

// Error boundary para lazy loading
class LazyLoadErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType },
  { hasError: boolean; retryCount: number }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ComponentType }) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('LazyLoad Error:', error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('LazyLoad Error Details:', { error, errorInfo });
  }

  handleRetry = () => {
    if (this.state.retryCount < 3) {
      this.setState({
        hasError: false,
        retryCount: this.state.retryCount + 1
      });
      // Force reload da página se necessário
      if (this.state.retryCount >= 2) {
        window.location.reload();
      }
    }
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Erro ao Carregar Página
            </h2>
            <p className="text-gray-600 mb-4">
              Houve um problema ao carregar esta página. Isso pode ser devido a uma atualização do sistema.
            </p>
            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {this.state.retryCount >= 2 ? 'Recarregar Página' : 'Tentar Novamente'}
              </button>
              {FallbackComponent && (
                <div className="mt-4">
                  <FallbackComponent />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Tentativa {this.state.retryCount + 1} de 3
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const lazyLoad = (Component: React.ComponentType): ReactNode => (
  <LazyLoadErrorBoundary>
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  </LazyLoadErrorBoundary>
);

export default function AppRouter() {
  return (
    <RouterProvider
      router={createBrowserRouter(
        createRoutesFromElements(
          <Route path="/" element={<Layout />}>
            <Route index element={<RootRoute />} />
            <Route path="login" element={lazyLoad(Login)} />
            <Route path="register" element={lazyLoad(Register)} />
            <Route path="forgot-password" element={lazyLoad(ForgotPassword)} />
            <Route path="set-password" element={lazyLoad(SetPassword)} />
            <Route path="verify-email" element={lazyLoad(VerifyEmail)} />
            <Route path="register/success" element={lazyLoad(RegistrationSuccess)} />
            <Route path="subscription-offers" element={lazyLoad(SubscriptionOffers)} />
            <Route path="my-plans" element={<ProtectedRoute>{lazyLoad(MyPlans)}</ProtectedRoute>} />

            {/* Payment Callback Routes */}
            <Route path="payment/success" element={lazyLoad(PaymentSuccess)} />
            <Route path="payment/cancelled" element={lazyLoad(PaymentCancelled)} />
            <Route path="payment/expired" element={lazyLoad(PaymentExpired)} />

            {/* Extra Income Routes */}
            <Route path="extra-income">
              <Route
                path="introduction"
                element={<ProtectedRoute>{lazyLoad(ExtraIncomeIntroduction)}</ProtectedRoute>}
              />
              <Route
                path="questionnaire"
                element={<ProtectedRoute>{lazyLoad(ExtraIncomeQuestionnaire)}</ProtectedRoute>}
              />
              <Route
                path="suggestions"
                element={<ProtectedRoute>{lazyLoad(ExtraIncomeSuggestions)}</ProtectedRoute>}
              />
              <Route
                path="strategies"
                element={<ProtectedRoute>{lazyLoad(ExtraIncomeStrategies)}</ProtectedRoute>}
              />
            </Route>

            {/* Business Plan Routes */}
            <Route path="business-plan">
              <Route path="new" element={<ProtectedRoute>{lazyLoad(BusinessPlanNew)}</ProtectedRoute>} />
              <Route path=":planId">
                <Route
                  path="executive-summary"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanExecutiveSummary)}</ProtectedRoute>}
                />
                <Route
                  path="mission-vision-values"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanMissionVisionValues)}</ProtectedRoute>}
                />
                <Route
                  path="products-services"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanProductsServices)}</ProtectedRoute>}
                />
                <Route
                  path="market-analysis"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanMarketAnalysis)}</ProtectedRoute>}
                />
                <Route
                  path="marketing-sales"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanMarketingSales)}</ProtectedRoute>}
                />
                <Route
                  path="operational-structure"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanOperationalStructure)}</ProtectedRoute>}
                />
                <Route
                  path="risks-expansion"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanRisksExpansion)}</ProtectedRoute>}
                />
                <Route
                  path="challenges-objectives"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanChallengesObjectives)}</ProtectedRoute>}
                />
                <Route
                  path="finances"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanFinances)}</ProtectedRoute>}
                />
                <Route
                  path="action-plan"
                  element={<ProtectedRoute>{lazyLoad(BusinessPlanActionPlan)}</ProtectedRoute>}
                />
              </Route>
            </Route>

            {/* Finance Management Routes */}
            <Route path="finance-management" element={<ProtectedRoute>{lazyLoad(FinanceManagementLayout)}</ProtectedRoute>}>
              <Route
                path="dashboard"
                element={lazyLoad(FinanceManagementDashboard)}
              />
              <Route
                path="banks-goals"
                element={lazyLoad(BanksGoals)}
              />
              <Route
                path="transactions"
                element={lazyLoad(TransactionHistory)}
              />
            </Route>

            <Route path="*" element={lazyLoad(NotFound)} />
          </Route>
        )
      )}
    />
  );
}
